/** How much of the body of a call the browser has sent, in bytes. */
export type UploadProgress = (sent: number, total: number) => void

function headersOf(xhr: XMLHttpRequest): Headers {
  const headers = new Headers()
  for (const line of xhr
    .getAllResponseHeaders()
    .trim()
    .split(/[\r\n]+/)) {
    const at = line.indexOf(':')
    if (at > 0) headers.append(line.slice(0, at).trim(), line.slice(at + 1).trim())
  }
  return headers
}

/** The statuses that have no body: `Response` refuses a body with them. */
const NO_BODY: ReadonlySet<number> = new Set([204, 205, 304])

/**
 * A `fetch` for one call that says how much of the body is sent. `fetch`
 * itself cannot say that, so the call goes through `XMLHttpRequest`. The
 * call is the same in every other way: its headers, its cookies, its abort
 * signal. A call that the network or the browser stops rejects, as `fetch`
 * does.
 */
export function fetchWithProgress(
  onProgress: UploadProgress,
): (request: Request) => Promise<Response> {
  return async (request) => {
    const payload = await request.blob()
    return new Promise<Response>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open(request.method, request.url)
      xhr.withCredentials = request.credentials === 'include'
      // Every answer of the API is JSON text, or has no body.
      xhr.responseType = 'text'
      request.headers.forEach((value, name) => {
        xhr.setRequestHeader(name, value)
      })
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) onProgress(event.loaded, event.total)
      })
      xhr.addEventListener('load', () => {
        resolve(
          new Response(NO_BODY.has(xhr.status) ? null : xhr.responseText, {
            status: xhr.status,
            statusText: xhr.statusText,
            headers: headersOf(xhr),
          }),
        )
      })
      xhr.addEventListener('error', () => {
        reject(new TypeError('The network failed while the files were sent.'))
      })
      xhr.addEventListener('abort', () => {
        reject(new DOMException('The upload was aborted.', 'AbortError'))
      })
      request.signal.addEventListener('abort', () => {
        xhr.abort()
      })
      xhr.send(payload)
    })
  }
}
